import { Translate } from 'module/hooks/use-translation';
import {
  faFileCsv,
  faFileJpg,
  faFilePdf,
  faFilePng,
  faFileText,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFileCode,
  faFileImage,
  faFileAlt,
  faFile,
  faCalendarLines,
  faEnvelope,
  faFileLines,
} from '@fortawesome/pro-regular-svg-icons';
import {
  KnowledgeBaseContent,
  KnowledgeBaseTreeFolder,
  KnowledgeBaseTreeFolderType,
} from 'api/knowledge-base/types';
import { PageType } from 'api/page-template/types';

export const getIconForFileType = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    // PDF files
    case 'pdf':
      return faFilePdf;

    // Image files
    case 'png':
      return faFilePng;
    case 'jpg':
    case 'jpeg':
      return faFileJpg;
    case 'gif':
    case 'bmp':
    case 'svg':
    case 'webp':
      return faFileImage;

    // Spreadsheet files
    case 'csv':
      return faFileCsv;
    case 'xls':
    case 'xlsx':
      return faFileExcel;

    // Document files
    case 'doc':
    case 'docx':
      return faFileWord;

    // Presentation files
    case 'ppt':
    case 'pptx':
      return faFilePowerpoint;

    // Code files
    case 'js':
    case 'ts':
    case 'jsx':
    case 'tsx':
    case 'css':
    case 'html':
    case 'htm':
    case 'xml':
    case 'yaml':
    case 'yml':
      return faFileCode;

    // Text files
    case 'txt':
    case 'json':
    case 'tsv':
      return faFileText;

    // Markdown files
    case 'md':
    case 'markdown':
      return faFileAlt;

    // Unknown files
    default:
      return faFile;
  }
};

export const getFileType = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return 'PDF document';
    case 'png':
      return 'PNG';
    case 'jpg':
      return 'JPG';
    case 'jpeg':
      return 'JPEG';
    case 'gif':
      return 'GIF';
    case 'bmp':
      return 'BMP';
    case 'svg':
      return 'SVG';
    case 'webp':
      return 'WebP';
    case 'csv':
      return 'CSV';
    case 'xls':
    case 'xlsx':
      return 'Excel';
    case 'doc':
    case 'docx':
      return 'Word document';
    case 'ppt':
    case 'pptx':
      return 'PowerPoint presentation';
    case 'txt':
      return 'Text file';
    case 'md':
      return 'Markdown';
  }

  return 'unknown';
};

export const getIconForAiPageType = (pageType: PageType) => {
  switch (pageType) {
    case PageType.Company:
      return faFileLines;
    case PageType.Meeting:
      return faCalendarLines;
    case PageType.Email:
      return faEnvelope;
    default:
      return faFileLines;
  }
};

export const getContentType = (content: KnowledgeBaseContent): string => {
  switch (content.contentType) {
    case 'FILE':
      return getFileType(content.attachment.name);
    case 'AI_PAGE':
      return content.template.pageType;
    default:
      return '';
  }
};
export const getFolderType = (folder: KnowledgeBaseTreeFolder): string => {
  switch (folder.folderType) {
    case 'DEFAULT':
      return '';
    case 'API_DOC':
      return 'Api doc';
    case 'WEBSITE':
      return 'Website';
    default:
      return '';
  }
};
export const getFolderTypeLabel = (
  type: KnowledgeBaseTreeFolderType,
  translate: Translate,
): string => {
  switch (type) {
    case 'DEFAULT':
      return '';
    case 'API_DOC':
      return translate('knowledge_base.folder.type.api_doc');
    case 'WEBSITE':
      return translate('knowledge_base.folder.type.website');
    default:
      return '';
  }
};
export const getAiPageType = (
  pageType: PageType,
  translate: Translate,
): string => {
  switch (pageType) {
    case PageType.Company:
      return translate('knowledge_base.ai_pages.grid.type.company');
    case PageType.Meeting:
      return translate('knowledge_base.ai_pages.grid.type.meeting');
    case PageType.Email:
      return translate('knowledge_base.ai_pages.grid.type.email');
    default:
      return '';
  }
};
